import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { useOrderPerformanceFeatures } from '../../../../../providers/OrderPerformanceFeaturesProvider';
import { OrderCompletionProgressHeading } from './OrderCompletionProgress';

/**
 * 统一的订单 Modal Header 组件
 */
const OrderModalHeader = ({
	orderId,
	customerInfo,
	isLoading = false,
	isCompleted = false,
	approvalStatus,
	badges = [],
	autoAddPendingApprovalBadge = true,
	extraInfo = null,
}) => {
	const { order } = useOrderContext();
	const { orderDetailUi } = useOrderPerformanceFeatures();
	const { isDark } = useSelector((state) => state.config);
	const headerRef = useRef(null);
	const [showGlassShadow, setShowGlassShadow] = useState(false);
	const isEffectivelyCompleted =
		!order?.uiHints?.isPendingFinalCostConfirmation &&
		(Boolean(isCompleted) || Boolean(order?.uiHints?.isLegacyCompletedBeforeFinalCostCutoff) || Boolean(order?.isFinalCostConfirmed));
	const useWhiteClose = isDark;
	const showCompletionProgress = Boolean(orderDetailUi && order?._id && !isLoading);
	const displayOrderId = orderId || '-';
	const customerName = customerInfo?.customerName?.trim() || '';
	const canCopyOrderId = Boolean(orderId);
	const canCopyCustomerName = Boolean(customerName);

	/** 与首页 NavbarTop 一致：body 下滑后挂 Falcon 底阴影 */
	useEffect(() => {
		const headerEl = headerRef.current;
		if (!headerEl) return undefined;

		const modalContent = headerEl.closest('.modal-content');
		if (!modalContent) return undefined;

		let scrollEl = null;

		const updateGlassShadow = () => {
			if (!scrollEl) {
				setShowGlassShadow(false);
				return;
			}
			setShowGlassShadow((scrollEl.scrollTop ?? 0) > 0);
		};

		const findScrollEl = () =>
			modalContent.querySelector('.order-fullscreen-modal__scroll .simplebar-content-wrapper');

		const attach = () => {
			const next = findScrollEl();
			if (!next) return false;
			if (scrollEl === next) {
				updateGlassShadow();
				return true;
			}
			if (scrollEl) {
				scrollEl.removeEventListener('scroll', updateGlassShadow);
			}
			scrollEl = next;
			scrollEl.addEventListener('scroll', updateGlassShadow, { passive: true });
			updateGlassShadow();
			return true;
		};

		if (attach()) {
			return () => {
				scrollEl?.removeEventListener('scroll', updateGlassShadow);
				setShowGlassShadow(false);
			};
		}

		const observer = new MutationObserver(() => {
			if (attach()) observer.disconnect();
		});
		observer.observe(modalContent, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
			scrollEl?.removeEventListener('scroll', updateGlassShadow);
			setShowGlassShadow(false);
		};
	}, [isLoading]);

	const copyText = useCallback(async (text, successMessage) => {
		const value = String(text ?? '').trim();
		if (!value) return;
		try {
			await navigator.clipboard.writeText(value);
			toast.success(successMessage);
		} catch (error) {
			toast.error('复制失败，请重试');
		}
	}, []);

	const handleCopyOrderId = useCallback(() => {
		copyText(orderId, '订单编号已复制');
	}, [copyText, orderId]);

	const handleCopyCustomerName = useCallback(() => {
		copyText(customerName, '客户名称已复制');
	}, [copyText, customerName]);

	const allBadges = [...badges];
	if (autoAddPendingApprovalBadge && approvalStatus === 'inProcessOfApproval') {
		const hasPendingApprovalBadge = allBadges.some((badge) => badge.text === '待审批' || badge.text === '申请中');
		if (!hasPendingApprovalBadge) {
			allBadges.unshift({ bg: 'danger', text: '待审批' });
		}
	}

	return (
		<Modal.Header
			ref={headerRef}
			closeButton
			closeVariant={useWhiteClose ? 'white' : undefined}
			className={classNames('order-modal-header px-x1 py-2', {
				'modal-header-celebration order-modal-header--celebration': isEffectivelyCompleted,
				'order-modal-header--default': !isEffectivelyCompleted,
				'order-modal-header--glass-shadow': showGlassShadow,
			})}
		>
			{isLoading ? (
				<div className='order-modal-header__content' aria-hidden='true' />
			) : (
				<div className='order-modal-header__content'>
					<Modal.Title className='order-modal-header__title mb-0'>
						<div className='order-modal-header__title-row'>
							<div className='order-modal-header__order-id-wrap'>
								<h5 className='order-modal-header__order-id mb-0' title={displayOrderId}>
									{displayOrderId}
								</h5>
								{canCopyOrderId ? (
									<Button
										type='button'
										variant='link'
										size='sm'
										className='order-modal-header__copy-btn p-0'
										title='复制订单编号'
										aria-label='复制订单编号'
										onClick={handleCopyOrderId}
									>
										<FontAwesomeIcon icon='copy' />
									</Button>
								) : null}
							</div>
							{allBadges.length > 0 ? (
								<div className='order-modal-header__badges'>
									{allBadges.map((badge, index) => (
										<Badge key={index} bg={badge.bg} className='order-modal-header__badge fs-10'>
											{badge.text}
										</Badge>
									))}
								</div>
							) : null}
						</div>

						{customerName ? (
							<p
								className={classNames('order-modal-header__customer mb-0 fs-10 fw-normal', {
									'mt-1': !showCompletionProgress,
									'text-white text-opacity-90': isEffectivelyCompleted && isDark,
								})}
							>
								<span className='order-modal-header__customer-wrap'>
									<span>
										客户名称：
										<span className={isEffectivelyCompleted && isDark ? 'text-white' : 'text-primary'}>{customerName}</span>
									</span>
									{canCopyCustomerName ? (
										<Button
											type='button'
											variant='link'
											size='sm'
											className='order-modal-header__copy-btn p-0'
											title='复制客户名称'
											aria-label='复制客户名称'
											onClick={handleCopyCustomerName}
										>
											<FontAwesomeIcon icon='copy' />
										</Button>
									) : null}
								</span>
							</p>
						) : null}

						{extraInfo}
					</Modal.Title>

					{showCompletionProgress ? (
						<div
							className={classNames('order-modal-header__progress', {
								'order-modal-header__progress--celebration': isEffectivelyCompleted,
								'order-modal-header__progress--dark': isDark && !isEffectivelyCompleted,
							})}
						>
							<OrderCompletionProgressHeading onCelebration={isEffectivelyCompleted} isDark={isDark} />
						</div>
					) : null}
				</div>
			)}
		</Modal.Header>
	);
};

export default OrderModalHeader;
